import { useEffect, useRef } from "react";
import { BarcodeReader } from "../types/BarcodeReader";
import { BarcodeReaderStatus } from "../types/HoneywellBarcodeReaderStatusTypes";
import {
  honeywellFailedToCloseBarcodeReaderError,
  honeywellFailedToCreateBarcodeReaderError,
} from "../helpers/scannerSentryErrorHandlerUtils";
import trackerHelper from "@coworker/app/src/helpers/tracker";

declare global {
  interface Window {
    BarcodeReader: {
      new (
        config: null, //According to the Honeywell documentation, the config can be null, default or an empty string, both all but null breaks it.
        onComplete: (result: honeywellBarcodeResultType) => void
      ): BarcodeReader;
    };
  }
}

export type honeywellBarcodeResultType = {
  status: BarcodeReaderStatus;
  message?: string;
};

export function useBarcodeReader(
  onData: (data: string, type: string, time: string) => void
) {
  const readerRef = useRef<BarcodeReader | null>(null);
  const barcodeRead = (data: string, type: string, time: string) => {
    trackerHelper.trackHoneywellScannerUsed();
    onData(data, type, time);
  };
  useEffect(() => {
    const onBarcodeReaderComplete = (result: honeywellBarcodeResultType) => {
      if (result.status === BarcodeReaderStatus.Success) {
        const reader = readerRef.current;
        if (!reader) return;
        reader.addEventListener("barcodedataready", barcodeRead, false);
      } else {
        honeywellFailedToCreateBarcodeReaderError(result);
      }
    };
    const openBarcodeReader = () => {
      if (!readerRef.current && window.BarcodeReader) {
        readerRef.current = new window.BarcodeReader(
          null,
          onBarcodeReaderComplete
        );
      }
    };

    const closeBarcodeReader = () => {
      const reader = readerRef.current;
      if (reader) {
        reader.close((result: honeywellBarcodeResultType) => {
          if (result.status === BarcodeReaderStatus.Success) {
            readerRef.current = null;
          } else {
            honeywellFailedToCloseBarcodeReaderError(result);
          }
        });
      }
    };

    openBarcodeReader();
    return () => {
      closeBarcodeReader();
    };
  }, [onData]);
}
